import { Extension } from "@tiptap/core"
import { Fragment, Slice } from "@tiptap/pm/model"
import { Plugin } from "@tiptap/pm/state"

import type { Mark, Node as PmNode, Schema } from "@tiptap/pm/model"

/**
 * A pasted plain-text block, shaped the way the same text would be typed: a
 * blank line starts a paragraph, a single newline is a line break inside one.
 *
 * ProseMirror's own parser splits on `/(?:\r\n?|\n)+/` and makes every line its
 * own paragraph, which cuts hard-wrapped prose mid-sentence and swallows the
 * blank lines between real paragraphs. A reply pasted from anywhere that wraps
 * its text then left the composer as a run of one-line paragraphs.
 */
export function plainTextSlice(
	text: string,
	schema: Schema,
	marks: readonly Mark[] = [],
): Slice {
	const paragraph = schema.nodes.paragraph
	const hardBreak = schema.nodes.hardBreak
	if (!paragraph) return Slice.empty

	const blocks = text.replace(/\r\n?/g, "\n").split(/\n[ \t]*\n+/)
	const paragraphs: PmNode[] = []
	blocks.forEach((block, blockIndex) => {
		// Paragraph margins are zero everywhere a message body renders, so the
		// blank line between two paragraphs is an empty paragraph of its own.
		if (blockIndex > 0) paragraphs.push(paragraph.create())
		const inline: PmNode[] = []
		block.split("\n").forEach((line, index) => {
			if (index > 0 && hardBreak) inline.push(hardBreak.create())
			if (line) inline.push(schema.text(line, marks as Mark[]))
		})
		paragraphs.push(paragraph.create(null, Fragment.fromArray(inline)))
	})

	// Open on both sides, so the first paragraph joins the one the cursor is in
	// and the last joins whatever follows, exactly as typing would.
	return new Slice(Fragment.fromArray(paragraphs), 1, 1)
}

export const PlainTextPaste = Extension.create({
	name: "plainTextPaste",
	addProseMirrorPlugins() {
		return [
			new Plugin({
				props: {
					clipboardTextParser: (text, $context) =>
						plainTextSlice(text, $context.doc.type.schema, $context.marks()),
				},
			}),
		]
	},
})
